/**
 * The draw list a screen compiles down to — the seam AD-009 puts between the
 * menu model and the render backend. Every op is plain data in logical
 * 320×200 coordinates with palette *indices*, never CSS colors, so the ops a
 * screen produces can be asserted in a test without a canvas, and a future
 * hi-res backend can reinterpret the same list against redrawn art.
 */

export const LOGICAL_WIDTH = 320;
export const LOGICAL_HEIGHT = 200;

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** One horizontal run of pixels — how the non-rectangular banner is described. */
export interface Span {
  readonly x: number;
  readonly y: number;
  readonly w: number;
}

/** The four atlases the shell draws with: `FONT2` banner legends, `FONT1` button labels and headings, `FONT3` rows, labels and prose, `TALK` the debriefing narrative. */
export type FontId = "FONT1" | "FONT2" | "FONT3" | "TALK";

/**
 * How a string is inked. The original's raised-panel text is a two-offset
 * emboss, exact against the capture: draw the glyph, then the same glyph one
 * row lower — pixels only in the first get `shadow`, pixels in both get
 * `body`, pixels only in the second get `highlight`. Leaving `shadow`
 * undefined gives the flat single-color text the `MAIN MENU` banner uses.
 */
export interface TextStyle {
  readonly body: number;
  readonly shadow?: number;
  readonly highlight?: number;
}

/**
 * Which dithered ramp fills a region. The board ramp is a screen-space
 * diagonal — the same one under every board on every screen, which is why it
 * takes no parameters. The banner ramp is *banner-local* (its trough sits 20px
 * in from whichever banner it fills), so it carries that banner's left edge.
 */
export type FieldStyle = { readonly kind: "board" } | { readonly kind: "banner"; readonly left: number };

export const BOARD_FIELD: FieldStyle = { kind: "board" };

export function bannerField(left: number): FieldStyle {
  return { kind: "banner", left };
}

export type DrawOp =
  | { readonly kind: "image"; readonly asset: string; readonly x: number; readonly y: number }
  | {
      readonly kind: "sprite";
      readonly sheet: string;
      readonly frame: number;
      readonly x: number;
      readonly y: number;
      readonly flipH: boolean;
    }
  | { readonly kind: "fill"; readonly rect: Rect; readonly color: number }
  | { readonly kind: "field"; readonly style: FieldStyle; readonly spans: readonly Span[] }
  | {
      readonly kind: "text";
      readonly font: FontId;
      readonly text: string;
      readonly x: number;
      readonly y: number;
      readonly style: TextStyle;
    };

export function fill(rect: Rect, color: number): DrawOp {
  return { kind: "fill", rect, color };
}

export function spanRun(x: number, y: number, w: number, color: number): DrawOp {
  return fill({ x, y, w, h: 1 }, color);
}

/**
 * One bevel ring: the outline of `rect`, with its top row, bottom row and
 * two side columns colored independently. Every raised element in the
 * original shell — the board, the header banner, each button — is a stack of
 * these around a filled core, which is why this is the only frame primitive.
 */
export function ringRect(rect: Rect, top: number, bottom: number, side: number): DrawOp[] {
  const { x, y, w, h } = rect;
  if (w <= 0 || h <= 0) return [];
  if (h === 1) return [fill({ x, y, w, h: 1 }, top)];
  return [
    fill({ x, y, w, h: 1 }, top),
    fill({ x, y: y + h - 1, w, h: 1 }, bottom),
    fill({ x, y: y + 1, w: 1, h: h - 2 }, side),
    fill({ x: x + w - 1, y: y + 1, w: 1, h: h - 2 }, side),
  ];
}

/** `[top, bottom, side]` triples, outermost first. */
export type BevelStack = readonly (readonly [number, number, number])[];

export function bevelRings(rect: Rect, stack: BevelStack): DrawOp[] {
  return stack.flatMap((ring, depth) =>
    ringRect({ x: rect.x + depth, y: rect.y + depth, w: rect.w - 2 * depth, h: rect.h - 2 * depth }, ring[0], ring[1], ring[2]),
  );
}

export function insetRect(rect: Rect, by: number): Rect {
  return { x: rect.x + by, y: rect.y + by, w: rect.w - 2 * by, h: rect.h - 2 * by };
}

export function rectContains(rect: Rect, x: number, y: number): boolean {
  return x >= rect.x && y >= rect.y && x < rect.x + rect.w && y < rect.y + rect.h;
}

function bitReverse3(value: number): number {
  const v = value & 7;
  return ((v & 1) << 2) | (v & 2) | ((v >> 2) & 1);
}

/**
 * The board's field: a dithered diagonal ramp, lightest (`226`) to darkest
 * (`237`) and back, running at dx/dy = 1.5 with a 228px horizontal period.
 * Fitted against `main-menu.png` — slope, period and amplitude are the
 * measured values; the dither *pattern* is an 8-step ordered approximation of
 * whatever `PS.EXE` actually used, so ~40% of field pixels land one ramp step
 * off the original (see this lane's report). Nothing structural depends on it.
 */
export const BOARD_PHASE = 415;

export function boardFieldIndex(x: number, y: number, phase: number = BOARD_PHASE): number {
  const t = (((2 * x - 3 * y - phase) % 456) + 456) % 456;
  const triangle = 1 - Math.abs(t - 228) / 228;
  const value = 226 + 11.4 * triangle;
  const base = Math.floor(value);
  return Math.min(237, base + ((bitReverse3(t) + 0.5) / 8 < value - base ? 1 : 0));
}

const BAYER4 = [
  [0.0625, 0.5625, 0.1875, 0.6875],
  [0.8125, 0.3125, 0.9375, 0.4375],
  [0.25, 0.75, 0.125, 0.625],
  [1.0, 0.5, 0.875, 0.375],
];

/**
 * The header banner's field, in banner-local coordinates: a shallow V whose
 * trough (`226`) sits 20px in from the banner's left edge, falling into it at
 * 0.076 index/px and climbing out at 0.107. Fitted across all three captured
 * banners at once — `MAIN MENU` (78px wide), `CAREER MENU` (88) and
 * `FLY SINGLE MISSION` (121) — which is what shows the ramp is anchored to the
 * banner rather than stretched across it: all three read 227½ at `left+3` and
 * 226 at `left+20`, then diverge only because the wider ones have further to
 * climb (`main-menu.png` ends at 232, `fly-single-mission.png` at 236).
 */
export function bannerFieldIndex(x: number, y: number, left: number): number {
  const u = x - left;
  const value = 226 + (u < 20 ? (20 - u) * 0.076 : (u - 20) * 0.107);
  const base = Math.floor(value);
  const threshold = BAYER4[y & 3]?.[x & 3] ?? 0.5;
  return base + (threshold < value - base ? 1 : 0);
}

export function fieldIndex(style: FieldStyle, x: number, y: number): number {
  return style.kind === "board" ? boardFieldIndex(x, y) : bannerFieldIndex(x, y, style.left);
}
